import React, { useMemo } from "react";

import type { EmotionalIntentSegment } from "./NarrativeSonics.types";

export interface EmotionalArcOverlayProps {
  viewportStartSec: number;
  viewportEndSec: number;
  pxPerSec: number;
  scrollLeft: number;
  segments: EmotionalIntentSegment[];
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function segmentColors(segment: EmotionalIntentSegment) {
  const intensity = clamp(segment.intensity, 0, 1);
  const urgency = clamp(segment.urgency, 0, 1);

  if (segment.valence >= 0) {
    return {
      fill: `linear-gradient(180deg, rgba(45,212,191,${0.14 + intensity * 0.24}), rgba(34,211,238,${0.08 + urgency * 0.18}))`,
      edge: `rgba(94,234,212,${0.26 + intensity * 0.28})`,
      label: "rgba(209,250,229,0.92)",
      glow: `rgba(20,184,166,${0.12 + urgency * 0.18})`,
    };
  }

  return {
    fill: `linear-gradient(180deg, rgba(251,113,133,${0.12 + intensity * 0.2}), rgba(251,191,36,${0.06 + urgency * 0.18}))`,
    edge: `rgba(253,164,175,${0.24 + intensity * 0.26})`,
    label: "rgba(255,228,230,0.92)",
    glow: `rgba(244,63,94,${0.1 + urgency * 0.18})`,
  };
}

export default function EmotionalArcOverlay({
  viewportStartSec,
  viewportEndSec,
  pxPerSec,
  scrollLeft,
  segments,
}: EmotionalArcOverlayProps): JSX.Element | null {
  const visibleSegments = useMemo(
    () =>
      segments.filter(
        (segment) => segment.endSec >= viewportStartSec && segment.startSec <= viewportEndSec,
      ),
    [segments, viewportEndSec, viewportStartSec],
  );

  if (visibleSegments.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-[28]" data-oc-emotional-arc-overlay="1" aria-hidden>
      <div className="absolute left-0 right-0 top-3 h-10 rounded-2xl border border-white/6 bg-white/[0.015]" />
      {visibleSegments.map((segment) => {
        const leftPx = Math.max(0, segment.startSec * pxPerSec);
        const widthPx = Math.max(8, (segment.endSec - segment.startSec) * pxPerSec);
        const colors = segmentColors(segment);
        const intensityHeight = 10 + clamp(segment.intensity, 0, 1) * 14;
        const urgencyTop = 12 - clamp(segment.urgency, 0, 1) * 4;

        return (
          <div
            key={segment.id}
            className="absolute rounded-[16px] border"
            style={{
              left: leftPx,
              top: urgencyTop,
              width: widthPx,
              height: intensityHeight,
              borderColor: colors.edge,
              background: colors.fill,
              boxShadow: `0 0 0 1px ${colors.edge}, 0 10px 22px ${colors.glow}`,
            }}
          >
            {widthPx >= 88 ? (
              <div
                className="truncate px-2 pt-1 text-[9px] font-black uppercase tracking-[0.16em]"
                style={{ color: colors.label }}
              >
                ARC {segment.intensity.toFixed(2)}
              </div>
            ) : null}
          </div>
        );
      })}

      <div className="sr-only">scrollLeft {Math.round(scrollLeft)}</div>
    </div>
  );
}